import * as Sentry from "@sentry/nextjs";
import { useRouter } from "next/router";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { useAuth } from "stores/auth/auth-context";
import { Modal } from "design-system/components/practice/modal/modal";
import type {
  PracticePointsData,
  PracticeSessionData
} from "services/api/practice/practiceService.types";
import PracticeLoadingImage from "public/assets-v2/illustrations/loading.svg";
import PracticeSubmissionImage from "public/assets-v2/illustrations/submission_loading.svg";
import { PracticeAnswerSubmission } from "../practice-answer-submission/practice-answer-submission";
import {
  PracticeCamera,
  PracticeCameraRef
} from "../practice-camera/practice-camera";
import { PracticeCrop, PracticeCropRef } from "../practice-crop/practice-crop";
import { PracticeErrorScreen } from "../practice-error/practice-error";
import { ExitBlocker } from "../practice-exit-blocker/exit-blocker";
import { PracticeFinished } from "../practice-finished/practice-finished";
import { PracticeLoading } from "../practice-loading/practice-loading";
import {
  PracticeResult,
  ResultVariant
} from "../practice-result/practice-result";
import { PracticeSubmit } from "../practice-submit/practice-submit";
import { TutorialContent } from "../practice-tutorial/tutorial-content";
import { SessionCard } from "../session-card/session-card";
import {
  advancePracticeQuestion,
  completePracticeSession,
  loadPracticePoints,
  loadPracticeState,
  submitImageAttempt,
  submitMcqAttempt
} from "./practice-session.api";
import { getSubscriptionStatus } from "services/api/sessions/sessionsService";
import { SubscriptionType } from "services/api/sessions/sessionService.types";
import {
  trackPracticeAnswerImageSubmitted,
  trackPracticeCompleted,
  trackPracticeExplanationViewed,
  trackPracticeError,
  trackPracticeHowToSolveOpened,
  trackPracticeMcqAttempted,
  trackPracticeQuestionCompleted,
  trackPracticeQuestionOpened,
  trackPracticeSessionExited
} from "services/api/app-events/practiceEvents";
import { SessionWrapper } from "./practice-session.styles";
import { usePracticeNavigation } from "stores/practice/practice-navigation-context";

type SessionStep =
  | "fetching"
  | "fetching_mcq"
  | "question"
  | "camera"
  | "crop"
  | "submitting"
  | "finished";

type ActiveModal = null | "exit" | "submission" | "result" | "tutorial";

const MCQ_LOADING_DURATION_MS = 3000;
const INITIAL_LOADING_DELAY_MS = 2000;

const getImageResultVariant = (
  isLegible: boolean,
  isRelevant: boolean,
  isCorrect: boolean
): ResultVariant => {
  // if (!isLegible) return "notLegible";
  // if (!isRelevant) return "notRight";
  return isCorrect ? "correct" : "incorrect";
};

export const PracticeSession: React.FC = () => {
  const router = useRouter();
  const { user } = useAuth();
  const { registerPracticeExitHandler } = usePracticeNavigation();
  const accessToken = user?.userData?.primary_user?.access_token ?? null;

  useEffect(() => {
    if (!router.isReady || accessToken) return;

    router.replace({
      pathname: "/login",
      query: router.query
    });
  }, [accessToken, router]);

  const [sessionStep, setSessionStep] = useState<SessionStep>("fetching");
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null
  );
  const [sessionData, setSessionData] = useState<PracticeSessionData | null>(
    null
  );
  const [sessionPoints, setSessionPoints] = useState<PracticePointsData | null>(
    null
  );
  const [sessionError, setSessionError] = useState(false);
  const [resultVariant, setResultVariant] = useState<ResultVariant>("correct");
  const [resultPoints, setResultPoints] = useState(0);
  const [answerImage, setAnswerImage] = useState<string>("");
  const [isMcqOpen, setIsMcqOpen] = useState(false);
  const [isScreenPaused, setIsScreenPaused] = useState(false);
  const cameraRef = useRef<PracticeCameraRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cropRef = useRef<PracticeCropRef>(null);
  const mcqTimerRef = useRef<number | null>(null);
  const requestIdRef = useRef(0);
  const mutationPendingRef = useRef(false);

  useEffect(() => {
    return registerPracticeExitHandler((destination) => {
      if (sessionStep === "finished") {
        void router.push(destination);
        return;
      }

      setPendingNavigation(destination);
      setActiveModal("exit");
    });
  }, [registerPracticeExitHandler, router, sessionStep]);

  const reportError = useCallback(
    (error: unknown, operation: string) => {
      Sentry.captureException(error, {
        tags: { screen: "PracticeSession", operation }
      });
      trackPracticeError(
        accessToken,
        "PracticeSession",
        error instanceof Error ? error.message : String(error),
        operation
      );
      setActiveModal(null);
      setSessionError(true);
    },
    [accessToken]
  );

  const loadSession = useCallback(
    async (startIfNeeded = false) => {
      if (!accessToken) return;

      const requestId = ++requestIdRef.current;
      setSessionError(false);
      setSessionStep("fetching");

      try {
        let isSubscriptionPaused = false;
        try {
          const { data } = await getSubscriptionStatus(accessToken);
          isSubscriptionPaused =
            data.subscriptionStatus.subscriptionType ===
            SubscriptionType.PAUSED;
        } catch (subscriptionError) {
          if (requestId !== requestIdRef.current) return;

          Sentry.captureException(subscriptionError, {
            tags: {
              screen: "PracticeSession",
              operation: "check-subscription"
            }
          });
          isSubscriptionPaused = true;
        }

        if (requestId !== requestIdRef.current) return;
        if (isSubscriptionPaused) {
          await router.replace({
            pathname: "/latihan",
            query: { subscriptionPaused: "true" }
          });
          return;
        }

        const state = await loadPracticeState(accessToken, startIfNeeded);
        if (requestId !== requestIdRef.current) return;

        await new Promise((resolve) =>
          window.setTimeout(resolve, INITIAL_LOADING_DELAY_MS)
        );
        if (requestId !== requestIdRef.current) return;

        if (!state) {
          await router.replace("/latihan");
          return;
        }

        setSessionData(state);
        setSessionPoints(null);
        setIsMcqOpen(false);
        setSessionStep("question");
        trackPracticeQuestionOpened(
          accessToken,
          state.current_question.id,
          state.current_question_number
        );
      } catch (error) {
        if (requestId === requestIdRef.current) {
          reportError(error, "load-session");
        }
      }
    },
    [accessToken, reportError, router]
  );

  useEffect(() => {
    if (!router.isReady || !accessToken) return;

    // Delaying one task prevents React Strict Mode's setup/cleanup probe from
    // sending the start-next-question mutation twice in development.
    const loadTimer = window.setTimeout(() => {
      loadSession(router.query.shouldStart === "true");
    }, 0);

    return () => {
      window.clearTimeout(loadTimer);
      requestIdRef.current += 1;
    };
  }, [accessToken, loadSession, router.isReady, router.query.shouldStart]);

  const showCropWithImage = (image: string) => {
    setAnswerImage(image);
    setActiveModal(null);
    setSessionStep("crop");
  };

  const handleOpenMcq = () => {
    if (mcqTimerRef.current !== null) return;

    setSessionStep("fetching_mcq");

    mcqTimerRef.current = window.setTimeout(() => {
      setIsMcqOpen(true);
      setSessionStep("question");
      mcqTimerRef.current = null;
    }, MCQ_LOADING_DURATION_MS);
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result !== "string") return;

      showCropWithImage(reader.result);
    };

    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const handleCropSubmit = async () => {
    const image = cropRef.current?.getCroppedImage();

    if (!image || !sessionData || !accessToken || mutationPendingRef.current)
      return;

    mutationPendingRef.current = true;
    const answerAttempt = sessionData.current_question.attempts.length + 1;
    setSessionStep("submitting");
    setSessionError(false);

    try {
      const { result, session } = await submitImageAttempt(
        accessToken,
        sessionData,
        image
      );

      setSessionData(session);
      setResultPoints(result.point);
      setResultVariant(
        getImageResultVariant(
          result.is_legible,
          result.is_relevant,
          result.is_correct
        )
      );
      trackPracticeAnswerImageSubmitted(
        accessToken,
        sessionData.current_question.id,
        sessionData.current_question_number,
        answerAttempt,
        result.is_correct
      );
      setSessionStep("question");
      setActiveModal("result");
    } catch (error) {
      reportError(error, "submit-image");
    } finally {
      mutationPendingRef.current = false;
    }
  };

  const handleNextQuestion = async () => {
    if (!accessToken || !sessionData || mutationPendingRef.current) return;

    mutationPendingRef.current = true;
    trackQuestionCompleted(sessionData);
    setSessionStep("fetching");
    setSessionError(false);

    try {
      const nextSession = await advancePracticeQuestion(accessToken);
      setSessionData(nextSession);
      setAnswerImage("");
      setIsMcqOpen(false);
      setSessionStep("question");
      trackPracticeQuestionOpened(
        accessToken,
        nextSession.current_question.id,
        nextSession.current_question_number
      );
    } catch (error) {
      reportError(error, "next-question");
    } finally {
      mutationPendingRef.current = false;
    }
  };

  const handleMcqSubmit = async (answerId: string) => {
    if (!accessToken || !sessionData || mutationPendingRef.current) return;

    mutationPendingRef.current = true;
    const answerAttempt = sessionData.current_question.attempts.length + 1;
    setSessionStep("submitting");
    setSessionError(false);

    try {
      const { result, session } = await submitMcqAttempt(
        accessToken,
        sessionData,
        answerId
      );
      setSessionData(session);
      setResultPoints(result.point);
      setResultVariant(result.is_correct ? "correctMcq" : "incorrectMcq");
      trackPracticeMcqAttempted(
        accessToken,
        sessionData.current_question.id,
        sessionData.current_question_number,
        answerAttempt,
        result.is_correct,
        answerId
      );
      setIsMcqOpen(false);
      setSessionStep("question");
      setActiveModal("result");
    } catch (error) {
      reportError(error, "submit-mcq");
    } finally {
      mutationPendingRef.current = false;
    }
  };

  const finishSession = async () => {
    if (!accessToken || !sessionData || mutationPendingRef.current) return;

    mutationPendingRef.current = true;
    trackQuestionCompleted(sessionData);
    setSessionStep("fetching");
    setSessionError(false);

    try {
      await completePracticeSession(accessToken);
      setSessionStep("finished");
    } catch (error) {
      reportError(error, "finish-session");
    } finally {
      mutationPendingRef.current = false;
    }
  };

  useEffect(() => {
    if (sessionStep !== "finished" || !accessToken || !sessionData) return;

    let cancelled = false;
    setSessionPoints(null);

    void loadPracticePoints(accessToken, sessionData.id)
      .then((points) => {
        if (cancelled) return;

        setSessionPoints(points);
        trackPracticeCompleted(
          accessToken,
          points.total_points,
          points.correct_answer_points,
          points.effort_points
        );
      })
      .catch((error) => {
        if (cancelled) return;

        Sentry.captureException(error, {
          tags: { screen: "PracticeSession", operation: "load-points" }
        });
        trackPracticeError(
          accessToken,
          "PracticeSession",
          error instanceof Error ? error.message : String(error),
          "load-points"
        );
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, sessionData, sessionStep]);

  const handleResultAction = () => {
    setActiveModal(null);

    if (resultVariant === "notLegible" || resultVariant === "notRight") {
      setSessionStep("camera");
      return;
    }

    if (resultVariant === "correct" || resultVariant === "incorrect") {
      if (sessionData) {
        const lastAttempt =
          sessionData.current_question.attempts[
            sessionData.current_question.attempts.length - 1
          ];
        trackPracticeExplanationViewed(
          accessToken,
          sessionData.current_question.id,
          sessionData.current_question_number,
          lastAttempt?.attempt_number ?? 0,
          resultVariant === "correct"
        );
      }
      return;
    }
    if (!sessionData) return;

    if (
      sessionData.current_question_number >= sessionData.number_of_questions
    ) {
      finishSession();
    } else {
      handleNextQuestion();
    }
  };

  const trackQuestionCompleted = (session: PracticeSessionData) => {
    const attempts = session.current_question.attempts;
    const lastAttempt = attempts[attempts.length - 1];
    if (!lastAttempt) return;

    const finalAttemptResult =
      lastAttempt.attempt_type === "mcq"
        ? lastAttempt.is_correct
          ? "mcq_correct"
          : "mcq_incorrect"
        : `work_correct_a${lastAttempt.attempt_number}`;

    trackPracticeQuestionCompleted(
      accessToken,
      session.current_question.id,
      session.current_question_number,
      lastAttempt.attempt_number,
      finalAttemptResult
    );
  };

  const handleTutorial = () => {
    if (sessionData) {
      const attempts = sessionData.current_question.attempts;
      trackPracticeHowToSolveOpened(
        accessToken,
        sessionData.current_question.id,
        sessionData.current_question_number,
        attempts[attempts.length - 1]?.attempt_number ?? attempts.length
      );
    }
    setActiveModal("tutorial");
  };

  useEffect(() => {
    return () => {
      if (mcqTimerRef.current !== null) {
        window.clearTimeout(mcqTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsScreenPaused(document.hidden);
    };

    handleVisibilityChange();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  const renderSessionScreen = () => {
    if (sessionError) {
      return (
        <PracticeErrorScreen
          onRetry={() => loadSession(false)}
          onContactSupport={() => router.push("/hotline-kakaksiaga")}
        />
      );
    }

    switch (sessionStep) {
      case "fetching":
        return (
          <PracticeLoading
            headerImage={PracticeLoadingImage}
            title="Setiap latihan bikin kamu selangkah lebih maju"
            subtitle="Sebentar ya, latihanmu lagi disiapin.."
          />
        );
      case "question":
        if (!sessionData) return null;

        return (
          <SessionCard
            key={sessionData.current_question.id}
            sessionData={sessionData}
            accessToken={accessToken}
            onTutorial={() => setActiveModal("tutorial")}
            onExit={() => setActiveModal("exit")}
            onTakePhoto={() => setActiveModal("submission")}
            onFinish={finishSession}
            onNextQuestion={handleNextQuestion}
            onOpenMcq={handleOpenMcq}
            onSubmitMcq={handleMcqSubmit}
            isMcqOpen={isMcqOpen}
            blockAudio={activeModal !== null || isScreenPaused}
          />
        );
      case "camera":
        return (
          <PracticeAnswerSubmission
            onCapture={() => cameraRef.current?.startCapture()}
            onCancel={() => setSessionStep("question")}
            onTutorial={handleTutorial}
          >
            <PracticeCamera
              ref={cameraRef}
              onImageCaptured={showCropWithImage}
            />
          </PracticeAnswerSubmission>
        );

      case "crop":
        return (
          <PracticeAnswerSubmission
            onCapture={handleCropSubmit}
            onCancel={() => setSessionStep("question")}
            onTutorial={handleTutorial}
            primaryLabel="Kirim Foto"
            showCaptureIcon={false}
          >
            <PracticeCrop
              ref={cropRef}
              image={answerImage}
              onCropChange={() => {}}
            />
          </PracticeAnswerSubmission>
        );

      case "submitting":
        return (
          <PracticeLoading
            title="Kak Caca lagi pahami jawaban kamu..."
            headerImage={PracticeSubmissionImage}
            subtitle="Sebentar ya..."
          />
        );

      case "fetching_mcq":
        return (
          <PracticeLoading
            headerImage={PracticeLoadingImage}
            title="Setiap latihan bikin kamu selangkah lebih maju"
            banner="Tenang aja! Yuk coba pilihan ganda biar jawaban kamu bisa dicek lebih akurat"
            subtitle="Sebentar ya, latihanmu lagi disiapin.."
          />
        );

      case "finished":
        return (
          <PracticeFinished
            totalPoints={sessionPoints?.total_points}
            correctPoints={sessionPoints?.correct_answer_points}
            effortPoints={sessionPoints?.effort_points}
            onViewRanking={() => {
              router.push("/latihan");
            }}
          />
        );
    }
  };

  useLayoutEffect(() => {
    const lock = () => {
      const shouldLockSession =
        activeModal !== null ||
        !["question", "camera", "crop"].includes(sessionStep);

      if (window.innerWidth >= 768 || shouldLockSession) {
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
      } else {
        document.documentElement.style.overflow = "";
        document.body.style.overflow = "";
      }
    };

    lock();
    window.addEventListener("resize", lock);
    return () => {
      window.removeEventListener("resize", lock);
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, [activeModal, sessionStep]);
  return (
    <SessionWrapper>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png"
        hidden
        onChange={handleFileChange}
      />

      {renderSessionScreen()}

      <Modal
        size="md"
        isOpen={activeModal === "tutorial"}
        onClose={() => setActiveModal(null)}
        heading="Kirim Jawaban"
        closeIcon={false}
        closeOnOutsideClick={false}
      >
        <TutorialContent onClick={() => setActiveModal(null)} />
      </Modal>

      <Modal
        size="sm"
        isOpen={activeModal === "exit"}
        onClose={() => {
          setPendingNavigation(null);
          setActiveModal(null);
        }}
        closeIcon={true}
        closeOnOutsideClick={false}
      >
        <ExitBlocker
          onContinue={() => {
            setPendingNavigation(null);
            setActiveModal(null);
          }}
          onExit={() => {
            setActiveModal(null);
            const destination = pendingNavigation ?? "/latihan";
            setPendingNavigation(null);
            if (sessionData) {
              const attempts = sessionData.current_question.attempts;
              trackPracticeSessionExited(
                accessToken,
                sessionData.current_question.id,
                sessionData.current_question_number,
                attempts[attempts.length - 1]?.attempt_number ?? attempts.length
              );
            }
            void router.push(destination);
          }}
        />
      </Modal>

      <Modal
        size="lg"
        isOpen={activeModal === "submission"}
        onClose={() => setActiveModal(null)}
        closeIcon={true}
        heading="Kirim Jawaban"
        stickyHeader
        closeOnOutsideClick={false}
      >
        <PracticeSubmit
          onOpenCamera={() => {
            setActiveModal(null);
            setSessionStep("camera");
          }}
          onPickFile={openFilePicker}
          onClose={() => setActiveModal(null)}
        />
      </Modal>

      <Modal
        size="sm"
        isOpen={activeModal === "result"}
        onClose={() => setActiveModal(null)}
        closeIcon={true}
        closeOnOutsideClick={false}
      >
        <PracticeResult
          variant={resultVariant}
          points={resultPoints}
          isLastQuestion={
            sessionData
              ? sessionData.current_question_number >=
                sessionData.number_of_questions
              : false
          }
          onClick={handleResultAction}
          onClose={() => setActiveModal(null)}
        />
      </Modal>
    </SessionWrapper>
  );
};
